/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject } from '@angular/core';
import {
  ADD_FAMILY_INFORMATION_FACADE_TOKEN,
  IAddFamilyInformationFacade,
} from '../../../../../../data-access/src/lib/domain/facades-models/add-family-information-facade-interface';
import { GlobalConstants } from '../../../constants/global-constants';
import {
  AddFamilyInformationData,
  AddFamilyInformationReturnCodes,
  FamilyInformationItem,
} from '../../../models/family-information-model';
import { IFamilyInformationItem } from '../add-family-information-types';
import { SysqueSwitchButtonVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-switch-button-vm';
import { SysqueNumberBoxVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-number-box-vm';
import { PropertiesListItemsVM } from 'libs/sysque/shared/ui/src/lib/models/sysque-properties-list-vm';
import { AddFamilyProjectDataInfo } from '../../../../../../utils/src/lib/common-helper/add-family-project-data-info';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";
import { SysqueElectricalState } from '../../../models/sysque-electrical-state';

@Component({
  selector: 'sysque-hanger-specification-family-information',
  templateUrl: './hanger-specification-family-information.component.html',
  styleUrl: './hanger-specification-family-information.component.scss',
  standalone: false
})
export class HangerSpecificationFamilyInformation {
  public alertStatus = AlertStatus.Warning;
  public warningMessage = '';
  public title = '';
  public item: IFamilyInformationItem;
  public hangerButtonInfo: SysqueSwitchButtonVM;
  public propertiesListItems: PropertiesListItemsVM[] = [];

  public maxDiameterItems: SysqueNumberBoxVM[] = [];
  public maxDiameterSpinButtons = true;

  public maxWidthItems: SysqueNumberBoxVM[] = [];
  public maxWidthSpinButtons = true;

  public maxDepthItems: SysqueNumberBoxVM[] = [];
  public maxDepthSpinButtons = true;

  public rodDiameterItems: SysqueNumberBoxVM[] = [];
  public rodDiameterSpinButtons = true;

  public instanceDescriptionLabel = '';
  public instanceDescription = '';
  public instanceDescriptionHeight = '32px';
  public instanceDescriptionWidth = '295px';

  public typeDescriptionLabel = '';
  public typeDescription = '';
  public typeDescriptionHeight = '32px';
  public typeDescriptionWidth = '295px';

  public projectDataButtonInfo: SysqueSwitchButtonVM;
  public isProjectDataBtnDisabled = false;

  public hangerSpacingItems: SysqueNumberBoxVM[] = [];
  public hangerSpacingSpinButtons = true;

  public distanceFromFittingsItems: SysqueNumberBoxVM[] = [];
  public distanceFromFittingsSpinButtons = true;

  public distanceFromCouplingsItems: SysqueNumberBoxVM[] = [];
  public distanceFromCouplingsSpinButtons = true;

  public distanceFromEdgeOfBeamItems: SysqueNumberBoxVM[] = [];
  public distanceFromEdgeOfBeamSpinButtons = true;

  public rodLengthItems: SysqueNumberBoxVM[] = [];
  public rodLengthSpinButtons = true;

  public currentColor: string;
  public toggle = false;

  public noOverrideButtonInfo: SysqueSwitchButtonVM;

  public nominalSizesItems = [];
  public systemShape: string;

  public isAlertVisible = false;

  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  private get isMetric(): boolean {
    return this.item.unit === GlobalConstants.METRIC;
  }

  public get isConduit(): boolean {
    const conduitTypes = ['Conduit', 'FieldFabConduit'];
    return conduitTypes.includes(this.systemShape);
  }

  public get isCableTray(): boolean {
    const cableTrayTypes = ['Ladder Cable Tray', 'Channel Cable Tray', 'Wire Mesh Cable Tray', 'BusWay', 'WireWay'];
    return cableTrayTypes.includes(this.systemShape);
  }

  constructor(
    @Inject(ADD_FAMILY_INFORMATION_FACADE_TOKEN) private addFamilyInformationFacade: IAddFamilyInformationFacade
  ) {}

  public ngOnInit(): void {
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.addFamilyInformationFacade.registerControllers();
    const response = this.addFamilyInformationFacade.getFamilyInformationResponse();
    if (this.addFamilyInformationFacade.getHangerSetUpStatus()) {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
    } else if (response.Data.IsFromAdd) {
      this.item = this.addFamilyInformationFacade.setItemToAdd(response.Data);
      this.setFamilyInformationData(response.Data, this.item);
      this.handleMessages(response);
    } else {
      this.item = this.addFamilyInformationFacade.getFamilyInformationItem();
      this.setFamilyInformationData(response.Data, this.item);
      this.handleMessages(response);
    }
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.PROJECT_PARAMS, []);
    const state = this.addFamilyInformationFacade.getLocalStorageData(
      GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY
    ) as SysqueElectricalState;
    this.systemShape = state.categoryList[0].tabName;
    this.initData();
  }

  //#region Data initialization
  private initData(): void {
    this.titleInit();
    this.propertiesInit();
    if (this.isConduit) {
      this.maxDiameterInit();
    } else {
      this.maxWidthInit();
      this.maxDepthInit();
    }
    this.rodDiameterInit();
    this.instanceDescriptionInit();
    this.typeDescriptionInit();
    this.projectDataInit();
    this.hangerSpacingInit();
    this.distanceFromFittingsInit();
    this.distanceFromCouplingsInit();
    this.distanceFromEdgeOfBeamInit();
    this.rodLengthInit();
    this.hangerColorInit();
    this.nominalSizesInit();
    this.noOverrideInit();
  }

  private titleInit(): void {
    this.title = this.item.name;
  }

  private propertiesInit(): void {
    this.propertiesListItems = [
      {
        label: GlobalConstants.MANUFACTURER_LABEL,
        value: this.item.manufacturer,
        isEditable: false,
      },
      {
        label: GlobalConstants.DISCIPLINE_LABEL,
        value: this.item.supportDiscipline,
        isEditable: true,
      },
    ];
  }

  private maxDiameterInit(): void {
    this.maxDiameterItems = [
      {
        label: GlobalConstants.MAX_DIAMETER_LABEL,
        value: Number(this.item.diameter),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private maxWidthInit(): void {
    this.maxWidthItems = [
      {
        label: GlobalConstants.MAX_WIDTH_LABEL,
        value: Number(this.item.width),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private maxDepthInit(): void {
    this.maxDepthItems = [
      {
        label: GlobalConstants.MAX_DEPTH_LABEL,
        value: Number(this.item.depth),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private rodDiameterInit(): void {
    this.rodDiameterItems = [
      {
        label: GlobalConstants.ROD_DIAMETER_LABEL,
        value: Number(this.item.rodDiameter),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private instanceDescriptionInit(): void {
    this.instanceDescriptionLabel = GlobalConstants.INSTANCE_DESCRIPTION_LABEL;
    this.instanceDescription = this.item.descriptionInstance;
  }

  private typeDescriptionInit(): void {
    this.typeDescriptionLabel = GlobalConstants.TYPE_DESCRIPTION_LABEL;
    this.typeDescription = this.item.descriptionType;
  }

  private projectDataInit(): void {
    this.projectDataButtonInfo = {
      title: GlobalConstants.PROJECT_DATA_LABEL,
      enabled: Array.isArray(this.item.parameters) && this.item.parameters.length > 0,
      labelForEnabled: GlobalConstants.ENABLED,
      labelForDisabled: GlobalConstants.DISABLED,
      hasOptionButton: true,
    };
  }

  private hangerSpacingInit(): void {
    this.hangerSpacingItems = [
      {
        label: GlobalConstants.HANGER_SPACING_LABEL,
        value: Number(this.item.spacing),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private distanceFromFittingsInit(): void {
    this.distanceFromFittingsItems = [
      {
        label: GlobalConstants.DISTANCE_FROM_FITTINGS_LABEL,
        value: Number(this.item.distFromFittings === 'NA' ? '0' : this.item.distFromFittings),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private distanceFromCouplingsInit(): void {
    this.distanceFromCouplingsItems = [
      {
        label: GlobalConstants.DISTANCE_FROM_COUPLINGS_LABEL,
        value: Number(this.item.distFromCoupling === 'NA' ? '0' : this.item.distFromCoupling),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private distanceFromEdgeOfBeamInit(): void {
    this.distanceFromEdgeOfBeamItems = [
      {
        label: GlobalConstants.DISTANCE_FROM_EDGE_OF_BEAM_LABEL,
        value: Number(this.item.distFromBeam),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }
  private rodLengthInit(): void {
    this.rodLengthItems = [
      {
        label: GlobalConstants.ROD_LENGTH_LABEL,
        value: Number(this.item.errorHangerRodLength),
        isMetric: this.isMetric,
        height: '32',
        width: '110',
        minValue: 0
      },
    ];
  }

  private noOverrideInit(): void {
    this.noOverrideButtonInfo = {
      title: GlobalConstants.NO_OVERRIDE_LABEL,
      enabled: this.item.noOverride,
      labelForEnabled: GlobalConstants.Yes,
      labelForDisabled: GlobalConstants.NO,
      hasOptionButton: false,
    };
  }

  private hangerColorInit(): void {
    this.currentColor = this.addFamilyInformationFacade.convertIntARGBToRGBHex(Number(this.item.errorHangerColor));
  }

  private nominalSizesInit(): void {
    if (this.item.sizeRange == null || this.item.sizeRange === '') {
      return;
    }
    this.nominalSizesItems = this.item.sizeRange?.split('x');
    if (this.nominalSizesItems.length === 1 && this.nominalSizesItems[0].includes(',')) {
      this.nominalSizesItems = this.nominalSizesItems[0].split(',');
    }
  }

  //#endregion

  //#region Event handlers for controlers

  public onPropertiesValueChanged($event: { value: string; label: string }): void {
    switch ($event.label) {
      case GlobalConstants.MANUFACTURER_LABEL:
        this.item.manufacturer = $event.value.toString();
        break;
      case GlobalConstants.DISCIPLINE_LABEL:
        this.item.supportDiscipline = $event.value.toString();
        break;
    }
  }

  public onMaxDiameterChanged($event: { value: string; label: string }): void {
    this.item.diameter = $event.value;
  }

  public onMaxWidthChanged($event: { value: string; label: string }): void {
    this.item.width = $event.value;
  }

  public onMaxDepthChanged($event: { value: string; label: string }): void {
    this.item.depth = $event.value;
  }

  public onRodDiameterChanged($event: { value: string; label: string }): void {
    this.item.rodDiameter = $event.value;
  }

  public onInstanceDescriptionValueChanged($event: string): void {
    this.item.descriptionInstance = $event;
  }

  public onTypeDescriptionValueChanged($event: string): void {
    this.item.descriptionType = $event;
  }

  public onHangerSpacingChanged($event: { value: string; label: string }): void {
    this.item.spacing = $event.value;
  }

  public onDistanceFromFittingsChanged($event: { value: string; label: string }): void {
    this.item.distFromFittings = $event.value === '0' ? 'NA' : $event.value;
  }

  public onDistanceFromCouplingsChanged($event: { value: string; label: string }): void {
    this.item.distFromCoupling = $event.value === '0' ? 'NA' : $event.value;
  }

  public onDistanceFromEdgeOfBeamChanged($event: { value: string; label: string }): void {
    this.item.distFromBeam = $event.value;
  }

  public onRodLengthChanged($event: { value: string; label: string }): void {
    this.item.errorHangerRodLength = $event.value;
  }

  public onColorChange(color: string): void {
    this.currentColor = color;
    let argbValue = this.addFamilyInformationFacade.convertRGBHexToIntARGB(color)?.toString();
    if (argbValue === null || argbValue === undefined) {
      argbValue = this.addFamilyInformationFacade.convertRGBAStringToIntARGB(color)?.toString();
    }
    this.item.errorHangerColor = argbValue;
  }

  public onNoOverrideButtonClicked(button: SysqueSwitchButtonVM): void {
    this.item.noOverride = button.enabled;
  }

  // Method to get the label with the appropriate unit
  public getItemLabelWithUnit(item: string): string {
    return this.isMetric ? `${item}mm` : `${item}"`;
  }

  public doneClick(): void {
    if (!this.validateInput()) {
      return;
    }
    new AddFamilyProjectDataInfo(this.addFamilyInformationFacade).getModifiedProjectParameters(
      this.item,
      this.isProjectDataBtnDisabled
    );
    this.addFamilyInformationFacade.saveFamilyInformationInXml(this.item);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === 'familyInformationSaveResponse') {
      if (event.newValue === '') {
        return;
      }
      const response: AddFamilyInformationReturnCodes = JSON.parse(event.newValue);
      if (response == AddFamilyInformationReturnCodes.Success) {
        this.addFamilyInformationFacade.updateItem(this.item);
        this.addFamilyInformationFacade.sendClosePopupAction();
      } else if (response == AddFamilyInformationReturnCodes.HangerDiameterAlreadyExists) {
        this.showAlert(GlobalConstants.DIAMETER_ALREADY_EXISTS_IN_CURRENT_SPECIFICATION, AlertStatus.Warning);
      } else if (response == AddFamilyInformationReturnCodes.HangerWidthAndDepthAlreadyExists) {
        this.showAlert(GlobalConstants.WIDTH_AND_DEPTH_ALREADY_EXISTS_IN_CURRENT_SPECIFICATION, AlertStatus.Warning);
       } else if (response == AddFamilyInformationReturnCodes.HangerAddedSuccess) {
        this.needToOpenHangerSpecification();
        this.addFamilyInformationFacade.sendClosePopupAction();
      }
    }
  }

  public needToOpenHangerSpecification(): void {
    let hangerItem = this.addFamilyInformationFacade.getLocalStorageData(
      GlobalConstants.CopyCategoryHangerItems
    ) as IFamilyInformationItem[];
    if (Array.isArray(hangerItem) && hangerItem.length > 0) {
      // Create a new array to store items to be removed
      const itemsToRemove: any[] = [];

      // Iterate over each item and call facade.openHangerSpecification()

      this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.CopyHangerCategoryToSystem, {
        needToCopy: true,
        hangerItem: hangerItem[0],
      });
      itemsToRemove.push(hangerItem[0]);

      // Remove items from hangerItem after processing
      hangerItem = hangerItem.filter((item) => !itemsToRemove.includes(item));
      this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.CopyCategoryHangerItems, hangerItem);
    } else {
      this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.CopyHangerCategoryToSystem, {
        needToCopy: false,
        hangerItem: [],
      });
    }
  }

  /**
   * Handles the click event of the project data button.
   */
  public onProjectDataButtonClicked(): void {
    this.addFamilyInformationFacade.onClickedToOpenProjectData();
  }

  /**
   * Handles the click event of the project data switch button.
   * @param button - The SysqueSwitchButtonVM representing the clicked button.
   */
  public onProjectDataSwitchClicked(button: SysqueSwitchButtonVM): void {
    this.isProjectDataBtnDisabled = !button.enabled;
  }

  //#endregion

  private handleMessages(response: FamilyInformationItem): void {
    if (
      response.ReturnCode == AddFamilyInformationReturnCodes.Success ||
      response.ReturnCode == AddFamilyInformationReturnCodes.NoMessage
    ) {
      this.hideAlert();
    } else if (response.ReturnCode == AddFamilyInformationReturnCodes.HangerDiameterAlreadyExists) {
      this.showAlert(GlobalConstants.DIAMETER_ALREADY_EXISTS_IN_CURRENT_SPECIFICATION, AlertStatus.Warning);
    } else if (response.ReturnCode == AddFamilyInformationReturnCodes.HangerWidthAndDepthAlreadyExists) {
      this.showAlert(GlobalConstants.WIDTH_AND_DEPTH_ALREADY_EXISTS_IN_CURRENT_SPECIFICATION, AlertStatus.Warning);
    }
  }

  private validateInput(): boolean {
    if (this.isConduit && Number(this.item.diameter) <= 0) {
      this.showAlert(
        GlobalConstants.MAX_DIAMETER_LABEL + GlobalConstants.SHOULD_BE_GREATER_THAN_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (this.isCableTray && Number(this.item.width) <= 0) {
      this.showAlert(
        GlobalConstants.MAX_WIDTH_LABEL + GlobalConstants.SHOULD_BE_GREATER_THAN_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (this.isCableTray && Number(this.item.depth) <= 0) {
      this.showAlert(
        GlobalConstants.MAX_DEPTH_LABEL + GlobalConstants.SHOULD_BE_GREATER_THAN_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (Number(this.item.spacing) <= 0) {
      this.showAlert(
        GlobalConstants.HANGER_SPACING_LABEL + GlobalConstants.SHOULD_BE_GREATER_THAN_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (Number(this.item.distFromFittings) < 0) {
      this.showAlert(
        GlobalConstants.DISTANCE_FROM_FITTINGS_LABEL + GlobalConstants.SHOULD_BE_GREATER_OR_EQUAL_TO_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (Number(this.item.distFromCoupling) < 0) {
      this.showAlert(
        GlobalConstants.DISTANCE_FROM_COUPLINGS_LABEL + GlobalConstants.SHOULD_BE_GREATER_OR_EQUAL_TO_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    if (Number(this.item.errorHangerRodLength) <= 0) {
      this.showAlert(
        GlobalConstants.ROD_LENGTH_LABEL + GlobalConstants.SHOULD_BE_GREATER_THAN_ZERO,
        AlertStatus.Warning
      );
      return false;
    }
    return true;
  }

  private setFamilyInformationData(data: AddFamilyInformationData, item: IFamilyInformationItem): void {
    if (data === null || data === undefined) {
      return;
    }
    this.systemShape = data.SystemShape;
    item.diameter = data.Diameter;
    item.width = data.Width;
    item.depth = data.Depth;
    item.spacing = data.Spacing;
    item.distFromFittings = data.DistFromFittings;
    item.distFromCoupling = data.DistFromCouplings;
    item.distFromBeam = data.DistFromBeam;
    item.rodDiameter = data.RodDiameter;
    item.errorHangerRodLength = data.ErrHangRodLen;
    item.errorHangerColor = data.ErrHangColor;
    item.noOverride = data.NoOverride;
    item.familyCategoryName = data.FamilyCategoryName;
    item.specId = data.SpecId ?? item.specId;
    this.addFamilyInformationFacade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, item);
  }

  //#region Alert
  private showAlert(message: string, alertStatus: AlertStatus): void {
    this.warningMessage = message;
    this.alertStatus = alertStatus;
    this.isAlertVisible = true;
  }

  public hideAlert(): void {
    this.warningMessage = '';
    this.isAlertVisible = false;
  }

  //#endregion
}